/**
 * What the Back key does.
 *
 * The platform's own answer is "close the app", which is right exactly once — at the root
 * — and wrong everywhere else. So the app keeps its own depth count and only lets the
 * platform's answer through when that count is zero.
 *
 * Depth is counted rather than read from history, because `history.length` includes
 * entries from before the app loaded and never decreases. A counter that only this app
 * increments is the only number that means "screens I have opened".
 *
 * Handlers can also be pushed for transient UI — an open dialog, a player overlay — which
 * must swallow Back without navigating. Last one pushed wins, which is the same order
 * they are stacked on screen.
 */
import { useCallback, useEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { exitApp } from './keys';

type BackHandler = () => boolean;

const handlers: BackHandler[] = [];

/** What a Back press should do. */
export type BackOutcome = 'consumed' | 'navigate' | 'exit';

/**
 * The decision, as a pure function.
 *
 * Separated from the hook because it is an acceptance criterion in its own right — "Back
 * pops one level and exits at the root" — and because getting it wrong is expensive in
 * both directions. Too eager and the app quits from halfway down a series; too reluctant
 * and it can never be closed by remote at all, which on a television means pulling the
 * plug.
 *
 * Handlers run last-registered first, which is the order they are stacked on screen: a
 * dialog over a detail page must close before the page navigates.
 */
export function resolveBack(stack: readonly BackHandler[], depth: number): BackOutcome {
  for (let i = stack.length - 1; i >= 0; i--) {
    if (stack[i]!()) return 'consumed';
  }
  return depth > 0 ? 'navigate' : 'exit';
}

/**
 * Registers a handler that intercepts Back while mounted.
 *
 * Return true to consume the press, false to let it fall through to navigation.
 */
export function useBackHandler(handler: BackHandler, enabled = true): void {
  const ref = useRef(handler);
  ref.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const entry: BackHandler = () => ref.current();
    handlers.push(entry);
    return () => {
      const i = handlers.indexOf(entry);
      if (i >= 0) handlers.splice(i, 1);
    };
  }, [enabled]);
}

export function useBackStack(): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const depth = useRef(0);
  const lastKey = useRef(location.key);

  useEffect(() => {
    if (location.key === lastKey.current) return;
    lastKey.current = location.key;

    // PUSH deepens, POP surfaces, REPLACE leaves it alone — which is what makes a
    // redirect (login -> profile picker) not count as a screen to come back to.
    //
    // This used to add 1 on *every* change, which is why Back behaved so strangely: a
    // Back press decremented the counter and then the resulting POP incremented it
    // straight back, so depth never fell. Two screens deep the app thought it was three,
    // and Back from the player walked past the detail page and out to the menu.
    // Compared against the `NavigationType` members rather than the bare strings they
    // happen to equal: the strings work today only because the enum is string-valued, and
    // a typo in one would compile and silently never match.
    if (navigationType === NavigationType.Push) depth.current += 1;
    else if (navigationType === NavigationType.Pop) depth.current = Math.max(0, depth.current - 1);
  }, [location.key, navigationType]);

  return useCallback(() => {
    switch (resolveBack(handlers, depth.current)) {
      case 'consumed':
        return;
      case 'navigate':
        // The counter is not touched here. The POP this causes is what decrements it —
        // doing both would subtract twice, which is the other half of the bug above.
        //
        // `void`: react-router's `navigate` returns a promise that resolves once the
        // transition settles. Nothing here waits on it, and a rejection would be an
        // unhandled one, so the discard is written down rather than implied.
        void navigate(-1);
        return;
      case 'exit':
        // Root. This is the one press the platform's answer is right for.
        exitApp();
    }
  }, [navigate]);
}
